/**
 * Record a payment against an issued invoice.
 *
 * The amount defaults to the full outstanding balance, because that is what
 * happens most of the time and retyping a figure that is already on screen is
 * how a digit gets dropped.
 *
 * An **overpayment is refused by the API**, deliberately: money arriving that
 * is more than the invoice is a real event needing a decision — a refund, or
 * holding it on account — that an endpoint recording a payment cannot make for
 * somebody. The refusal names the outstanding amount, and it is shown as
 * written.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Alert,
  ApiError,
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  api,
  formatMoney,
  toast,
  todayISO,
} from '@connect/web-shared';

const schema = z.object({
  amount: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'An amount, to two decimal places.')
    .refine((value) => Number(value) > 0, 'A payment has to be more than nothing.'),
  received_on: z.string().min(1, 'When did it arrive?'),
  method: z.enum(['bank_transfer', 'card', 'cash', 'cheque', 'other']),
  reference: z.string().trim().max(120).optional(),
  notes: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

const METHOD_OPTIONS = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'card', label: 'Card' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

export function RecordPaymentDialog({
  open,
  onClose,
  onRecorded,
  invoicePublicId,
  invoiceCode,
  currency,
  outstanding,
}: {
  open: boolean;
  onClose: () => void;
  onRecorded?: () => void;
  invoicePublicId: string;
  invoiceCode: string;
  currency: string;
  /** The balance, as the decimal string the server sent. */
  outstanding: string;
}) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: outstanding,
      received_on: todayISO(),
      method: 'bank_transfer',
      reference: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      // The string the server sent, not a re-formatted number.
      amount: outstanding,
      received_on: todayISO(),
      method: 'bank_transfer',
      reference: '',
      notes: '',
    });
  }, [open, outstanding, form]);

  const record = useMutation({
    mutationFn: (values: FormValues) =>
      api.post(`/finance/invoices/${invoicePublicId}/payments`, {
        ...values,
        reference: values.reference?.trim() || null,
        notes: values.notes?.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Payment recorded');
      onRecorded?.();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (['amount', 'received_on', 'method', 'reference', 'notes'].includes(field)) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        // The overpayment refusal has no field — it is about the amount, so it
        // goes on the amount.
        if (!placed) form.setError('amount', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Payment against ${invoiceCode}`}
      description={`${formatMoney(outstanding, currency)} outstanding.`}
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={record.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={record.isPending}
            onClick={() => void form.handleSubmit((values) => record.mutateAsync(values))()}
          >
            Record it
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit((values) => record.mutateAsync(values))();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={`Amount (${currency})`}
            required
            inputMode="decimal"
            hint="Defaults to the full balance."
            error={form.formState.errors.amount?.message}
            {...form.register('amount')}
          />
          <Input
            label="Received"
            type="date"
            required
            error={form.formState.errors.received_on?.message}
            {...form.register('received_on')}
          />
          <Select
            label="How"
            options={METHOD_OPTIONS}
            error={form.formState.errors.method?.message}
            {...form.register('method')}
          />
          <Input
            label="Reference"
            placeholder="NEFT/0098213"
            hint="The bank reference, so this can be matched to a statement."
            error={form.formState.errors.reference?.message}
            {...form.register('reference')}
          />
        </div>

        <Textarea
          label="Notes"
          rows={2}
          error={form.formState.errors.notes?.message}
          {...form.register('notes')}
        />
      </form>
    </Modal>
  );
}
